import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { fetchSetup } from '@devexpress/analytics-core/analytics-utils';
import {
    DxReportDesigner as ReportDesignerComponent,
    RequestOptions,
    Callbacks,
    DesignerModelSettings,
    WizardSettings,
    DataSourceSettings,
    PreviewSettings,
    ParameterEditingSettings,
} from 'devexpress-reporting-react/dx-report-designer';
import {
    ExportSettings,
    ProgressBarSettings,
    SearchSettings,
} from 'devexpress-reporting-react/dx-report-viewer';
import { SearchBoxVisibilityMode } from 'devexpress-reporting/designer/utils/inititalizer';
import { useReporting } from '../providers/ReportingProvider.jsx';
import { DEFAULT_DESIGNER_HEIGHT } from '../config/defaults.js';
import { registerDevExpressRequestHeaders } from '../config/devexpress-request-headers.js';
import { configureDevExpressLocalization } from '../localization/configure-devexpress-localization.js';
import { useReportingVendorChromeScope } from '../hooks/useReportingVendorChromeScope.js';

const DEFAULT_WIZARD = {
    useMasterDetailWizard: true,
    enableJsonDataSource: true,
    enableSqlDataSource: true,
    enableObjectDataSource: true,
    enableFederationDataSource: true,
    reportWizardTemplatesSearchBoxVisibility: SearchBoxVisibilityMode.Auto,
};

const DEFAULT_DATA_SOURCE = {
    allowAddDataSource: true,
    allowEditDataSource: true,
    allowRemoveDataSource: true,
};

const DEFAULT_PARAMETER_EDITING = {
    allowReorderParameters: true,
    allowEditParameterCollection: true,
    allowEditProperties: true,
    allowEditParameterGroups: true,
    allowEditParameterSeparators: true,
};

const DEFAULT_EXPORT = {
    useAsynchronousExport: true,
    showPrintNotificationDialog: true,
};

const DEFAULT_PROGRESS = {
    keepOnVisibleArea: true,
};

const DEFAULT_SEARCH = {
    searchEnabled: true,
    useAsyncSearch: true,
};

/** DevExpress menu ActionId.Save — same handler as Ctrl+S (fires ReportSaved). */
const DX_SAVE_ACTION_ID = 'dxrd-save';

function unwrapKo(value) {
    if (value == null) return value;
    if (typeof value === 'function') {
        try {
            return value();
        } catch {
            return value;
        }
    }
    return value;
}

function getTabContext(tab) {
    if (!tab) return null;
    return unwrapKo(tab.context) ?? null;
}

function forceTabDirty(tab) {
    const flag = tab?.isDirty ?? tab?.isModified;
    if (typeof flag === 'function') {
        try {
            flag(true);
        } catch {
            // ignore
        }
    }
}

function clearTabDirty(tab) {
    const flag = tab?.isDirty ?? tab?.isModified;
    if (typeof flag === 'function') {
        try {
            flag(false);
        } catch {
            // ignore
        }
    }
}

function responseHeader(response, name) {
    if (!response) return '';
    if (typeof response.getResponseHeader === 'function') {
        return response.getResponseHeader(name) || '';
    }
    if (typeof response.headers?.get === 'function') {
        return response.headers.get(name) || '';
    }
    return '';
}

function saveReceiptFromResponse(response) {
    const editSessionId = responseHeader(response, 'X-Reporting-Designer-Edit-Session');
    const saveId = responseHeader(response, 'X-Reporting-Designer-Save-Id');
    const sha256 = responseHeader(response, 'X-Reporting-Designer-Sha256');
    const contractVersion = Number(responseHeader(response, 'X-Reporting-Designer-Contract-Version'));
    if (!editSessionId || !saveId || !sha256 || !Number.isInteger(contractVersion)) return null;
    return { editSessionId, saveId, sha256, contractVersion };
}

function saveRequestId(saveRequest) {
    if (!saveRequest || typeof saveRequest !== 'object') return null;
    const value = saveRequest.saveId;
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function saveRequestUrl(saveRequest, fallback) {
    if (typeof saveRequest === 'string' && saveRequest) return saveRequest;
    if (saveRequest?.url) return saveRequest.url;
    return fallback;
}


function getSaveAction(designer) {
    const storage = unwrapKo(designer?.GetButtonStorage?.());
    if (!storage || typeof storage !== 'object') return null;
    const action = storage[DX_SAVE_ACTION_ID];
    return typeof action === 'function' ? action : null;
}

/**
 * Web Report Designer — Report Wizard, Data Source Wizard (SQL/JSON/Object/Federation),
 * Veri kaynağı sihirbazı: SQL/JSON/Object/Federation ve MongoDB (ConnectionStrings, örn. SGRC); önizleme / dışa aktarma / arama dahil.
 *
 * Ref API:
 * - saveReport() → DevExpress menu Save (dxrd-save / Ctrl+S); ReportSaved + onSave tetiklenir
 * - getDesignerInstance() → JSReportDesigner
 *
 * @param {string} [reportUrl]
 * @param {number|string} [height]
 * @param {boolean} [useFullscreenWizard=true] — false: popup sihirbaz (UseFullscreenWizard)
 * @param {boolean} [allowMDI=true]
 * @param {boolean} [rightToLeft=false]
 * @param {Object} [wizardSettings] — WizardSettings üzerine merge
 * @param {Object} [dataSourceSettings]
 * @param {Object} [parameterEditingSettings]
 * @param {Object} [previewExportSettings] — ExportSettings
 * @param {Object} [previewProgressSettings] — ProgressBarSettings
 * @param {Object} [previewSearchSettings] — SearchSettings
 * @param {Object} [callbacks]
 * @param {Object} [requestOptions]
 * @param {(args: {report: object, serialized: object, url: string, designer: object, tab: object}) => (string|{url: string, saveId: string})} [saveAdapter]
 * @param {(args: object) => void} [onSave]
 * @param {() => void} [onExit]
 */
export const ReportDesigner = forwardRef(function ReportDesigner(
    {
        reportUrl,
        height = DEFAULT_DESIGNER_HEIGHT,
        useFullscreenWizard = true,
        allowMDI = true,
        rightToLeft = false,
        wizardSettings: wizardSettingsProp,
        dataSourceSettings: dataSourceSettingsProp,
        parameterEditingSettings: parameterEditingSettingsProp,
        previewExportSettings: previewExportSettingsProp,
        previewProgressSettings: previewProgressSettingsProp,
        previewSearchSettings: previewSearchSettingsProp,
        requestOptions: requestOptionsOverride,
        callbacks: callbacksOverride = {},
        saveAdapter,
        onSave,
        onExit,
    },
    ref,
) {
    const { designerRequestOptions, locale, translate } = useReporting();
    useReportingVendorChromeScope();
    const designerRef = useRef(null);
    const saveReportRef = useRef(null);
    const onSaveRef = useRef(onSave);
    const onExitRef = useRef(onExit);
    const callbacksOverrideRef = useRef(callbacksOverride);
    const reportStoragePatchRef = useRef(null);
    const reportStorageSetDataRef = useRef(null);
    const saveAdapterRef = useRef(saveAdapter);
    onSaveRef.current = onSave;
    onExitRef.current = onExit;
    callbacksOverrideRef.current = callbacksOverride;
    saveAdapterRef.current = saveAdapter;

    const getDesignerInstance = useCallback(() => designerRef.current?.instance?.(), []);

    const saveSerializedReport = useCallback(
        ({ designer, tab, report, serialized, url, invoke }) => {
            const saveRequest = saveAdapterRef.current?.({
                report,
                serialized,
                url,
                designer,
                tab,
            });
            const saveUrl = saveRequestUrl(saveRequest, url);
            const pending = invoke(serialized, saveUrl);
            const notifySaved = (response) => {
                clearTabDirty(tab);
                designer.ResetIsModified?.();
                onSaveRef.current?.({
                    report,
                    url,
                    Url: url,
                    reportUrl: url,
                    saveId: saveRequestId(saveRequest),
                    saveReceipt: saveReceiptFromResponse(response),
                });
            };
            if (pending && typeof pending.done === 'function') {
                return pending.done((_data, _textStatus, response) => notifySaved(response));
            }
            if (pending && typeof pending.then === 'function') {
                return pending.then((response) => {
                    notifySaved(response);
                    return response;
                });
            }
            return pending;
        },
        [],
    );

    const unpatchSaveStorage = useCallback(() => {
        const patch = reportStoragePatchRef.current;
        if (patch && patch.designer.ReportStorageSetData === patch.wrapped) {
            patch.designer.ReportStorageSetData = patch.original;
        }
        reportStoragePatchRef.current = null;
        reportStorageSetDataRef.current = null;
    }, []);

    const patchSaveStorage = useCallback(() => {
        const designer = getDesignerInstance();
        if (!designer || typeof designer.ReportStorageSetData !== 'function') return;
        if (reportStoragePatchRef.current?.designer === designer) return;

        unpatchSaveStorage();
        const original = designer.ReportStorageSetData;
        const wrapped = function wrappedReportStorageSetData(serialized, url) {
            if (typeof saveAdapterRef.current !== 'function') {
                return original.call(this, serialized, url);
            }
            const tab = designer.GetCurrentTab?.();
            const ctx = getTabContext(tab);
            const report = ctx?.report;
            if (!report || !url) return original.call(this, serialized, url);
            return saveSerializedReport({
                designer,
                tab,
                report,
                serialized,
                url,
                invoke: (nextSerialized, nextUrl) => original.call(this, nextSerialized, nextUrl),
            });
        };

        try {
            designer.ReportStorageSetData = wrapped;
        } catch {
            return;
        }

        reportStoragePatchRef.current = { designer, original, wrapped };
        reportStorageSetDataRef.current = original.bind(designer);
    }, [saveSerializedReport, unpatchSaveStorage]);

    /**
     * Persist draft like Ctrl+S: write layout to report storage, then fire onSave
     * (markDynamicReportDraft). Uses dxrd-save when storage APIs are unavailable.
     */
    const saveReport = () => {
        const designer = getDesignerInstance();
        if (!designer) return undefined;

        patchSaveStorage();

        const tab = designer.GetCurrentTab?.();
        if (tab) forceTabDirty(tab);

        const ctx = getTabContext(tab);
        const url = unwrapKo(ctx?.url) ?? unwrapKo(tab?.url);
        const report = ctx?.report;

        if (report && url && typeof report.serialize === 'function') {
            const invoke = reportStorageSetDataRef.current;
            if (typeof invoke === 'function') {
                return saveSerializedReport({
                    designer,
                    tab,
                    report,
                    serialized: report.serialize(),
                    url,
                    invoke,
                });
            }
        }

        if (saveAdapterRef.current) {
            throw new Error('The designer storage API is unavailable for the save adapter.');
        }

        const saveAction = getSaveAction(designer);
        if (saveAction) {
            return saveAction();
        }

        const bare = designer.SaveReport?.();
        if (bare && typeof bare.done === 'function') {
            bare.done(() => {
                clearTabDirty(tab);
                designer.ResetIsModified?.();
                onSaveRef.current?.({ report, url, Url: url, reportUrl: url });
            });
        }
        return bare;
    };

    saveReportRef.current = saveReport;

    const getSerializedReport = useCallback(() => {
        const designer = getDesignerInstance();
        const tab = designer?.GetCurrentTab?.();
        const report = getTabContext(tab)?.report;
        return typeof report?.serialize === 'function' ? report.serialize() : null;
    }, [getDesignerInstance]);

    useImperativeHandle(ref, () => ({
        saveReport: () => saveReportRef.current?.(),
        getDesignerInstance,
        getSerializedReport,
    }), [getDesignerInstance, getSerializedReport]);

    const finalRequestOptions = requestOptionsOverride
        ? {
            ...designerRequestOptions,
            ...requestOptionsOverride,
            headers: {
                ...(designerRequestOptions.headers || {}),
                ...(requestOptionsOverride.headers || {}),
            },
        }
        : designerRequestOptions;
    const {
        headers: requestHeaders,
        ...supportedRequestOptions
    } = finalRequestOptions;
    const requestHeadersSignature = JSON.stringify(requestHeaders || {});
    const [registeredHeadersSignature, setRegisteredHeadersSignature] = useState(null);

    useLayoutEffect(() => {
        const unregisterHeaders = registerDevExpressRequestHeaders(
            fetchSetup,
            JSON.parse(requestHeadersSignature),
        );
        setRegisteredHeadersSignature(requestHeadersSignature);
        return () => {
            unregisterHeaders();
        };
    }, [requestHeadersSignature]);

    // Gate only the initial mount. Later capability refreshes update the global
    // request headers without remounting the designer and discarding unsaved work.
    const requestHeadersReady = registeredHeadersSignature !== null;
    useLayoutEffect(() => {
        if (!requestHeadersReady) return undefined;
        patchSaveStorage();
        return unpatchSaveStorage;
    }, [patchSaveStorage, reportUrl, requestHeadersReady, unpatchSaveStorage]);


    const wizardMerged = {
        ...DEFAULT_WIZARD,
        ...wizardSettingsProp,
        useFullscreenWizard:
            wizardSettingsProp?.useFullscreenWizard ?? useFullscreenWizard,
    };

    const dataSourceMerged = { ...DEFAULT_DATA_SOURCE, ...dataSourceSettingsProp };
    const parameterMerged = { ...DEFAULT_PARAMETER_EDITING, ...parameterEditingSettingsProp };
    const exportMerged = { ...DEFAULT_EXPORT, ...previewExportSettingsProp };
    const progressMerged = { ...DEFAULT_PROGRESS, ...previewProgressSettingsProp };
    const searchMerged = { ...DEFAULT_SEARCH, ...previewSearchSettingsProp };

    const defaultCallbacks = {
        BeforeRender: (args) => {
            patchSaveStorage();
            callbacksOverrideRef.current?.BeforeRender?.(args);
        },
        CustomizeLocalization: (event) => {
            configureDevExpressLocalization(event, locale);
            callbacksOverrideRef.current?.CustomizeLocalization?.(event);
        },
        ReportSaving: (args) => {
            callbacksOverrideRef.current?.ReportSaving?.(args);
        },
        ReportSaved: (args) => {
            if (!saveAdapterRef.current) onSaveRef.current?.(args);
            callbacksOverrideRef.current?.ReportSaved?.(args);
        },
        ExitDesigner: () => {
            onExitRef.current?.();
            callbacksOverrideRef.current?.ExitDesigner?.();
        },
        CustomizeMenuActions: (args) => {
            callbacksOverrideRef.current?.CustomizeMenuActions?.(args);
            const actions = args?.args?.Actions || args?.Actions;
            if (saveAdapterRef.current && actions) {
                const saveAction = actions.find((action) => action.id === DX_SAVE_ACTION_ID);
                if (saveAction) saveAction.clickAction = () => saveReportRef.current?.();
                const saveAsAction = actions.find((action) => action.id === 'dxrd-saveas');
                if (saveAsAction) saveAsAction.visible = false;
            }
            if (onExitRef.current && actions) {
                actions.push({
                    text: translate('exitDesigner'),
                    imageClassName: 'dxrd-image-exit',
                    disabled: false,
                    visible: true,
                    clickAction: () => onExitRef.current?.(),
                });
            }
        },
        OnServerError: (args) => {
            callbacksOverrideRef.current?.OnServerError?.(args);
        },
    };

    const finalCallbacks = {
        ...callbacksOverride,
        ...defaultCallbacks,
        BeforeRender: defaultCallbacks.BeforeRender,
        CustomizeLocalization: defaultCallbacks.CustomizeLocalization,
        ReportSaving: defaultCallbacks.ReportSaving,
        ReportSaved: defaultCallbacks.ReportSaved,
        ExitDesigner: defaultCallbacks.ExitDesigner,
        CustomizeMenuActions: defaultCallbacks.CustomizeMenuActions,
        OnServerError: defaultCallbacks.OnServerError,
    };

    return (
        <div
            className="reporting-designer-container"
            style={{ width: '100%', height, position: 'relative' }}
            aria-busy={!requestHeadersReady}
        >
            {requestHeadersReady ? (
                <ReportDesignerComponent key={locale} ref={designerRef} reportUrl={reportUrl} height={height}>
                    <RequestOptions {...supportedRequestOptions} />
                    <DesignerModelSettings allowMDI={allowMDI} rightToLeft={rightToLeft}>
                        <WizardSettings {...wizardMerged} />
                        <DataSourceSettings {...dataSourceMerged} />
                        <ParameterEditingSettings {...parameterMerged} />
                        <PreviewSettings>
                            <ExportSettings {...exportMerged} />
                            <ProgressBarSettings {...progressMerged} />
                            <SearchSettings {...searchMerged} />
                        </PreviewSettings>
                    </DesignerModelSettings>
                    <Callbacks {...finalCallbacks} />
                </ReportDesignerComponent>
            ) : null}
        </div>
    );
});
